import { Button, EditableInfoBlock, Modal } from '@insurancemenu/imenu-components-lib';
import {
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';
import { useRootStore } from 'store/rootStore';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { useHistory } from 'react-router-dom';
import { toJS } from 'mobx';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ChevronDown, ChevronUp, Star } from '@insurancemenu/imenu-components-lib/dist/icons';
import { RateSegmentInfo, RATE_SEGMENT_INFO } from 'global/utils/RateSegmentHelper';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { domainServiceHelper } from 'global/services-helpers';
import { AncillaryProductLineCardStyled, createStyles } from './styles';
import ProductLineCard from '../../common/general/ProductLineCard';

interface Props {
  show: boolean;
  onClose: () => void;
  openPreviousForm: () => void;
  continueWithTemplate: (rateSegment: RateSegment) => void;
}

const ProductDetailsModal = observer((props: Props): JSX.Element => {
  const { show, onClose, openPreviousForm, continueWithTemplate } = props;
  const [showAncillaryPLines, setShowAncillaryPLines] = useState(true);
  // Mobx store
  const { browsingModalsStore, groupProposalsTableStore, domainStoreV2 } = useRootStore();
  const history = useHistory();
  const scrollRef = useRef<HTMLDivElement>(null);

  const convertedProductLines = browsingModalsStore.availableProductLines.map((line) => toJS(line)).slice();
  // Component actions
  // Redirect to the correct browsing experience
  const onProductLineClick = async (e: React.SyntheticEvent<HTMLLIElement>): Promise<void> => {
    const groupId = browsingModalsStore.rootStore.groupStore.group.id;
    const selectedProductLineId = e.currentTarget.dataset.productLine;
    const rateSegment = PRODUCT_LINES_CONTENT_MAP[selectedProductLineId as ProductLineId]?.rateSegment;

    await browsingModalsStore.requestCreateNewCart(rateSegment);

    scrollRef.current?.scrollTo(0, 0);
    groupProposalsTableStore.setShowProductDetailsModal(false);
    const cartId = browsingModalsStore.cart.id;

    switch (rateSegment) {
      case 'CUSTOM':
        history.push(`/initiate-rfq?cartId=${cartId}`);
        break;
      case 'GROUP':
      default:
        history.push(`/browsing?groupId=${groupId}&productLineId=${selectedProductLineId}&cartId=${cartId}`);
        break;
    }
  };

  const classes = createStyles(showAncillaryPLines);

  // Concomitant layout
  function getProductLinesButtons(productLineIds: ProductLineId[]): JSX.Element[] {
    return domainServiceHelper
      .filterGeneralPLines(productLineIds)
      .sort(sortByProductLineId)
      .map((pLineId) => (
        <ProductLineCard
          key={pLineId}
          pLineId={pLineId}
          onCardClick={onProductLineClick}
          icon={PRODUCT_LINES_CONTENT_MAP[pLineId]?.icon('4rem')}
          label={PRODUCT_LINES_CONTENT_MAP[pLineId]?.label}
        />
      ));
  }

  function getAncillaryProductLinesButtons(productLineIds: ProductLineId[]): JSX.Element[] {
    return domainServiceHelper
      .filterAncillaryPLines(productLineIds)
      .sort(sortByProductLineId)
      .map((pLineId) => (
        <AncillaryProductLineCardStyled
          key={pLineId}
          pLineId={pLineId}
          onCardClick={onProductLineClick}
          icon={PRODUCT_LINES_CONTENT_MAP[pLineId]?.icon('4rem')}
          label={PRODUCT_LINES_CONTENT_MAP[pLineId]?.label}
        />
      ));
  }

  function onModalClose(): void {
    onClose();
    setShowAncillaryPLines(false);
  }

  const uniquePlIds = [...new Set(convertedProductLines.map(({ rateSegment }) => rateSegment))].sort().reverse();

  const ProductLineSections = uniquePlIds.map((rateSegment) => {
    const productLineIds = convertedProductLines
      .filter((line) => line.rateSegment === rateSegment && rateSegment !== 'INDIVIDUAL')
      .map((line) => line.productLineId);

    return (
      <div key={rateSegment} className={classes.rateSegmentSection}>
        <p className={classes.rateSegmentTitle}>{RATE_SEGMENT_INFO[rateSegment as keyof RateSegmentInfo]?.title}</p>
        {getProductLinesButtons(productLineIds)?.length > 0 && (
          <ul className={classes.productLinesList} role="menu">
            {getProductLinesButtons(productLineIds)}
            {RATE_SEGMENT_INFO[rateSegment as keyof RateSegmentInfo]?.showFavoriteCard && // show card by Domain config
              // cover case that not all carriers allow to switch plan and because favorite proposals can have same product line we have to remove them
              (domainStoreV2.isChangePlanActionAvailable || browsingModalsStore.coveredProductLines.length === 0) && (
                <ProductLineCard
                  key="Favorites"
                  pLineId="Favorites"
                  onCardClick={(): void => {
                    continueWithTemplate(rateSegment);
                  }}
                  icon={<Star width="4rem" height="4rem" />}
                  label="Favorites"
                />
              )}
          </ul>
        )}
        {getAncillaryProductLinesButtons(productLineIds)?.length > 0 && (
          <div className={classes.ancillarySection}>
            <Button
              type="outlined"
              size="large"
              color="primary"
              className={classes.ancillaryButton}
              rightIconProp={showAncillaryPLines ? <ChevronUp /> : <ChevronDown />}
              onClick={(): void => {
                setShowAncillaryPLines(!showAncillaryPLines);
              }}
            >
              {showAncillaryPLines ? 'Hide Ancillary Product lines' : 'Show Ancillary Product lines'}
            </Button>
            {showAncillaryPLines && (
              <ul className={classes.productLinesList} role="menu">
                {getAncillaryProductLinesButtons(productLineIds)}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  });

  return (
    <Modal
      show={show}
      onClose={onModalClose}
      id="product-details"
      className={classes.modalSpacing}
      modalContainerRef={scrollRef}
    >
      <div className={classes.modalBody}>
        <h2 className={classes.title}>Product Details</h2>
        <EditableInfoBlock
          onEditInfo={openPreviousForm}
          title={`Effective date: ${normDateToShortAmericanDate(
            browsingModalsStore.form.fields.effectiveDate.value,
          )}, Zip: ${browsingModalsStore.form.fields.zipCode.value}`}
        />
        {convertedProductLines.length > 0 ? (
          <>
            <h3 className={classes.actionBlockTitle}>What product line would you like to browse first?</h3>
            <p className={classes.actionBlockCaption}>(You can browse others later)</p>
            <div className={classes.actionBlock}>{ProductLineSections}</div>
          </>
        ) : (
          <>
            <h3 className={classes.actionBlockTitle}>There are no available product lines to browse for this group</h3>
          </>
        )}
      </div>
      <FormsSpinner display={browsingModalsStore.loading} />
    </Modal>
  );
});

export default ProductDetailsModal;
